import React from 'react';
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';

// DataTable: TanStack Table powered table that uses semantic table tags so it
// matches existing Dashboard.css styles (sticky header, paddings, etc.).
//
// Props:
// - columns: TanStack column definitions
// - data: array
// - loading: boolean
// - emptyMessage: string
// - pageIndex: 0-based page index
// - pageSize: page size
// - totalElements: total rows
// - totalPages: total pages
// - onPageChange(newPageIndex)
// - onPageSizeChange(newSize)
// - sizes: optional page size options
//
const DataTable = ({
                       columns = [],
                       data = [],
                       loading = false,
                       emptyMessage = 'No records found',
                       pageIndex = 0,
                       pageSize = 10,
                       totalElements = 0,
                       totalPages = 0,
                       onPageChange = () => {},
                       onPageSizeChange = () => {},
                       sizes = [10, 20, 50, 100],
                   }) => {
    const [columnVisibility, setColumnVisibility] = React.useState({});
    const [showColumnToggle, setShowColumnToggle] = React.useState(false);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination: { pageIndex, pageSize },
            columnVisibility,
        },
        manualPagination: true,
        pageCount: totalPages || -1,
        onColumnVisibilityChange: setColumnVisibility,

    });

    const canPrev = pageIndex > 0;
    const canNext = totalPages ? pageIndex < totalPages - 1 : false;
    const start = totalElements === 0 ? 0 : pageIndex * pageSize + 1;
    const end = Math.min((pageIndex + 1) * pageSize, totalElements);

    // New logic to generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxPageButtons = 5; // You can adjust this number
        const startPage = Math.max(0, pageIndex - Math.floor(maxPageButtons / 2));
        const endPage = Math.min(totalPages - 1, startPage + maxPageButtons - 1);

        if (startPage > 0) {
            pages.push(0);
            if (startPage > 1) {
                pages.push('...');
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < totalPages - 1) {
            if (endPage < totalPages - 2) {
                pages.push('...');
            }
            pages.push(totalPages - 1);
        }
        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                <div className="column-visibility-toggle">
                    <button
                        className="column-toggle-btn-icon"
                        onClick={() => setShowColumnToggle(!showColumnToggle)}
                        title="Toggle Columns"
                    >
                        <i className="fas fa-columns"></i>
                    </button>
                    {showColumnToggle && (
                        <div className="column-toggle-menu">
                            {/* Your existing checkbox logic */}
                            {table.getAllLeafColumns().map(column => (
                                <div key={column.id}>
                                    <input
                                        type="checkbox"
                                        checked={column.getIsVisible()}
                                        onChange={column.getToggleVisibilityHandler()}
                                    />
                                    <label>{column.columnDef.header}</label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <table>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id} colSpan={header.colSpan}>
                                {header.isPlaceholder ? null : (
                                    flexRender(header.column.columnDef.header, header.getContext())
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {loading ? (
                    <tr>
                        <td colSpan={table.getAllLeafColumns().length} style={{ padding: 12 }}>Loading...</td>
                    </tr>
                ) : table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                                <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={table.getAllLeafColumns().length} style={{ textAlign: 'center', color: 'var(--pps-dark-gray)' }}>
                            {emptyMessage}
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
            <div className="pagination-bar">
                <div className="pagination-info">
                    {totalElements > 0 ? (
                        <span>Showing {start}-{end} of {totalElements}</span>
                    ) : (
                        <span>No records</span>
                    )}
                </div>

                <div className="pagination-controls">
                    <button
                        className="btn-secondary"
                        disabled={!canPrev}
                        onClick={() => onPageChange(pageIndex - 1)}
                    >
                        Prev
                    </button>

                    {pageNumbers.map((p, index) => (
                        p === '...' ? (
                            <span key={index} className="page-ellipsis">...</span>
                        ) : (
                            <button
                                key={p}
                                className={`page-number-btn ${p === pageIndex ? 'active' : ''}`}
                                onClick={() => onPageChange(p)}
                                disabled={p === pageIndex}
                            >
                                {p + 1}
                            </button>
                        )
                    ))}

                    <button
                        className="btn-secondary"
                        disabled={!canNext}
                        onClick={() => onPageChange(pageIndex + 1)}
                    >
                        Next
                    </button>
                </div>

                <div className="pagination-size">
                    <label>Rows per page:</label>
                    <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
                        {sizes.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
            </div>

        </>
    );
};

export default DataTable;